import { PlusIcon, SearchIcon } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@campai/lib-web/routes'
import {
    Button,
    InputGroup,
    InputGroupAddon,
    InputGroupInput,
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@campai/lib-web/components/new/ui'
import { type RouterOutput } from '@campai/lib-web/utils/trpc'

import { isTopicSortOption, topicSortOptions, type Search } from '@/product/community/category/routeUtils'

type CategoryData = RouterOutput['product']['community']['categories']['getCategory']

type ToolbarProps = {
    search: string
    setSearch: (search: string) => void
    submitSearch: () => void
    listSearch: Search
    category: CategoryData | null
    categoryId: string
    getListSearch: (overrides?: Partial<Search>) => Search
    canCreateTopic: boolean
    setTopicDialogOpen: (open: boolean) => void
}

export const Toolbar = observer(
    ({
        search,
        setSearch,
        submitSearch,
        listSearch,
        category,
        categoryId,
        getListSearch,
        canCreateTopic,
        setTopicDialogOpen,
    }: ToolbarProps) => {
        const { t } = useTranslation()
        const navigate = useNavigate()

        return (
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex w-full flex-wrap items-center gap-3 sm:w-auto">
                    <InputGroup className="min-w-0 max-w-none w-full sm:w-72">
                        <InputGroupInput
                            value={search}
                            onChange={(event) => setSearch(event.currentTarget.value)}
                            onBlur={submitSearch}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.currentTarget.blur()
                                }
                            }}
                            placeholder={t('product.community.category.searchPlaceholder')}
                        />
                        <InputGroupAddon>
                            <SearchIcon className="size-4" />
                        </InputGroupAddon>
                    </InputGroup>
                    <Select
                        value={listSearch.sort}
                        onValueChange={(value) => {
                            if (isTopicSortOption(value)) {
                                void navigate({
                                    to: '/product/community/categories/$categoryId',
                                    params: { categoryId },
                                    search: getListSearch({ page: 1, sort: value }),
                                })
                            }
                        }}
                    >
                        <SelectTrigger
                            aria-label={t('product.community.category.sort.label')}
                            className="bg-background w-44"
                        >
                            <SelectValue>{t(`product.community.category.sort.${listSearch.sort}`)}</SelectValue>
                        </SelectTrigger>
                        <SelectContent align="start">
                            <SelectGroup>
                                {topicSortOptions.map((sortOption) => (
                                    <SelectItem key={sortOption} value={sortOption}>
                                        {t(`product.community.category.sort.${sortOption}`)}
                                    </SelectItem>
                                ))}
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                    <div className="text-muted-foreground text-sm">
                        {category
                            ? t('product.community.home.categories.counts', {
                                  topics: category.topicsCount,
                                  posts: category.postsCount,
                              })
                            : t('product.community.category.loading')}
                    </div>
                </div>
                {canCreateTopic && (
                    <Button onClick={() => setTopicDialogOpen(true)}>
                        <PlusIcon data-icon="inline-start" />
                        {t('product.community.category.createTopic')}
                    </Button>
                )}
            </div>
        )
    }
)
